// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A presentational component that renders the report loader UI.
 *
 * Displays a checklist of analysis steps with visual indicators for
 * pending, active, and completed states. This component is stateless
 * and receives all display data via props.
 */
import {
  CheckCircleOutlined,
  RadioButtonUncheckedOutlined,
} from '@/constants/hardfin-standard-icons';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

/**
 * Props for the ReportLoaderDisplay component.
 */
interface ReportLoaderDisplayProps {
  currentStep: number;
  completedSteps: Set<number>;
  stepLabels: readonly string[];
}

/**
 * Renders the icon for a step based on its state.
 */
function StepIcon(props: { isComplete: boolean; isActive: boolean }) {
  const { isComplete, isActive } = props;

  if (isComplete) {
    return <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 20 }} />;
  }

  if (isActive) {
    return <CircularProgress size={18} thickness={5} />;
  }

  return (
    <RadioButtonUncheckedOutlined
      sx={{ color: 'text.disabled', fontSize: 20 }}
    />
  );
}

/**
 * Gets the text color for a step based on its state.
 */
function getStepColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return 'text.primary';
  }
  if (isActive) {
    return 'primary.main';
  }
  return 'text.disabled';
}

/**
 * Displays a pseudo-progress loader with a checklist of analysis steps.
 */
export function ReportLoaderDisplay(props: ReportLoaderDisplayProps) {
  const { currentStep, completedSteps, stepLabels } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          minWidth: 320,
          bgcolor: 'grey.50',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
          Analyzing assets...
        </Typography>

        <Stack spacing={1.5}>
          {stepLabels.map((label, index) => {
            const isComplete = completedSteps.has(index);
            const isActive = currentStep === index;

            return (
              <Stack
                key={label}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <StepIcon isComplete={isComplete} isActive={isActive} />
                <Typography
                  variant="body2"
                  sx={{
                    color: getStepColor(isComplete, isActive),
                    fontWeight: isActive ? 500 : 400,
                    transition: 'color 0.2s ease',
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Paper>
    </Box>
  );
}
